import { useEffect, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface Shortcut {
  keys: string[];
  action: () => void;
  description: string;
}

export function useKeyboardShortcuts() {
  const navigate = useNavigate();
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [pendingKey, setPendingKey] = useState<string | null>(null);

  const shortcuts: Shortcut[] = [
    { keys: ['g', 'd'], action: () => navigate('/'), description: 'Dashboard' },
    { keys: ['g', 'f'], action: () => navigate('/firms'), description: 'Firmalar' },
    { keys: ['g', 'b'], action: () => navigate('/dealers'), description: 'Bayiler' },
    { keys: ['g', 'u'], action: () => navigate('/users'), description: 'Kullanıcılar' },
    { keys: ['g', 'a'], action: () => navigate('/agents'), description: 'Agentlar' },
    { keys: ['g', 'v'], action: () => navigate('/agent-versions'), description: 'Agent Versiyonları' },
    { keys: ['g', 's'], action: () => navigate('/sources'), description: 'Veri Kaynakları' },
    { keys: ['g', 'k'], action: () => navigate('/api-keys'), description: 'API Keys' },
    { keys: ['g', 'l'], action: () => navigate('/logs'), description: 'Loglar' },
    { keys: ['g', 'n'], action: () => navigate('/announcements'), description: 'Duyurular' },
    { keys: ['?'], action: () => setIsHelpOpen(true), description: 'Yardım' },
  ];

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    // Ignore if typing in an input/textarea
    const target = event.target as HTMLElement;
    if (
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.isContentEditable
    ) {
      return;
    }

    const key = event.key.toLowerCase();

    // Handle ? for help
    if (key === '?' || (event.shiftKey && event.key === '/')) {
      event.preventDefault();
      setIsHelpOpen(true);
      return;
    }

    // Handle Escape to close help
    if (key === 'escape') {
      setIsHelpOpen(false);
      setPendingKey(null);
      return;
    }

    // Handle chord navigation (g + key)
    if (pendingKey === 'g') {
      const shortcut = shortcuts.find(
        s => s.keys.length === 2 && s.keys[0] === 'g' && s.keys[1] === key
      );
      if (shortcut) {
        event.preventDefault();
        shortcut.action();
      }
      setPendingKey(null);
      return;
    }

    // Start chord with 'g'
    if (key === 'g') {
      setPendingKey('g');
      // Clear pending key after timeout
      setTimeout(() => setPendingKey(null), 1000);
      return;
    }
  }, [pendingKey, shortcuts, navigate]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return {
    isHelpOpen,
    setIsHelpOpen,
    shortcuts,
    pendingKey,
  };
}
